import { inject, Injectable, Injector } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { CheckoutActions, CustomerActions, ShopCartActions } from '../actions';
import { selectUser } from '../auth/reducers';
import {
  ICompanyBusinessUnitAttributes,
  ICompanyBusinessUnits,
  ICustomerAccessControlData,
  ICustomerAddressData,
  ICustomerAddressesData,
  ICustomerAddressPayloadData,
  ICustomerCheckoutData,
  ICustomerInfo,
  ICustomerPreferences,
} from '../models/customer.models';
import { State } from '../reducers';
import * as CustomerSelectors from '../reducers/customer.reducer';
import { CustomerService } from '../services';
import { AppUtils } from '../utils/app.utils';
import { EUserRoles } from '../configurations/common';
import { IAddress, IPointOfContact } from '../models/common.models';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class CustomerFacade {
  private readonly injector: Injector = inject(Injector);
  private readonly store: Store<State> = inject(Store<State>);
  private readonly translate: TranslateService = inject(TranslateService);
  private pCustomerService: CustomerService | undefined;

  private getCustomerService(): CustomerService {
    if (!this.pCustomerService) {
      this.pCustomerService = this.injector.get(CustomerService);
    }
    return this.pCustomerService;
  }

  getCompanyUsers(): Observable<any> {
    return this.getCustomerService().getCompanyUsers();
  }

  selectCustomerData(): Observable<ICustomerCheckoutData> {
    return this.store.select(CustomerSelectors.selectCustomerData);
  }

  selectCustomerDataLoading(): Observable<boolean> {
    return this.store.select(CustomerSelectors.selectCustomerDataLoading);
  }

  selectCustomerPreferences(): Observable<ICustomerPreferences | null> {
    return this.store.select(CustomerSelectors.selectCustomerPreferences);
  }

  getCustomerCompanyRoles(): Observable<EUserRoles[]> {
    return this.store.select(CustomerSelectors.getCompanyRoles);
  }

  isBusinessPartner(): Observable<boolean> {
    return this.store.select(CustomerSelectors.isBusinessPartner);
  }

  isApprover(): Observable<boolean> {
    return this.store.select(CustomerSelectors.getCompanyRoles).pipe(
      map(roles => roles.includes(EUserRoles.Approver) || roles.includes(EUserRoles.Admin)
        || roles.includes(EUserRoles.BusinessPartnerApprover))
    );
  }

  getBusinessUnitById(id: string): Observable<ICompanyBusinessUnits> {
    return this.getCustomerService().getBusinessUnitById(id);
  }

  getCustomerConsentedTerms(): Observable<any> {
    return this.store.select(CustomerSelectors.getConsentedTerms);
  }

  getMyOrderPageData(): Observable<any> {
    return this.store.select(CustomerSelectors.getMyOrderPageData);
  }

  selectUserId(): Observable<string | undefined> {
    return this.store.select(CustomerSelectors.selectUserId);
  }

  selectIsGuest(): Observable<boolean> {
    return this.store.select(CustomerSelectors.isGuest);
  }

  selectIsViewer(): Observable<boolean> {
    return this.store.select(CustomerSelectors.isViewer);
  }

  selectIsCustomerLoaded(): Observable<boolean> {
    return this.store.select(CustomerSelectors.isCustomerLoaded);
  }

  selectAccessControlData(): Observable<ICustomerAccessControlData> {
    return this.store.select(CustomerSelectors.accessControlData);
  }

  selectCustomerCompanyRoles(): Observable<EUserRoles[]> {
    return this.store.select(CustomerSelectors.getCompanyRoles);
  }

  selectCompanyBusinessUnit(): Observable<any> {
    return this.store.select(CustomerSelectors.getMyBusinessUnitName);
  }

  selectCompanyRolesLoading(): Observable<boolean> {
    return this.store.select(CustomerSelectors.selectCompanyRolesLoading);
  }

  selectCustomerBusinessUnit(): Observable<{ customerBusinessUnit: any; companyRoles: EUserRoles[]; customerDataLoaded: boolean }> {
    return this.store.select(CustomerSelectors.getMyBusinessUnitAndUserRoles);
  }

  selectMyProfileInformation(): Observable<ICustomerInfo | null> {
    return this.store.select(CustomerSelectors.getMyProfileInformation);
  }

  selectCompanyUserId(): Observable<string | undefined> {
    return this.store.select(CustomerSelectors.selectCompanyUserId);
  }

  isSaturdayShipmentSelected(): Observable<boolean> {
    return this.store.select(CustomerSelectors.isSaturdayShipmentSelected);
  }

  setPointOfContact(data: IPointOfContact): void {
    this.store.dispatch(CustomerActions.setPointOfContact({data: data}));
  }

  clearPointOfContact(): void {
    this.store.dispatch(CustomerActions.clearPointOfContact());
  }

  clearAttentionTo(): void {
    this.store.dispatch(CustomerActions.clearAttentionTo());
  }

  getCustomerCompanyRolesFromLocalStorage(): string {
    const localStorage: Storage = globalThis.localStorage;

    if (!('customer' in localStorage)) {
      return '';
    }
    const customer: { companyRoles?: string } = JSON.parse(localStorage.customer);
    return customer.companyRoles || '';
  }

  clearDeliveryPagesData(cartId: string): void {
    this.store.dispatch(CustomerActions.clearCustomShipToAddress());
    this.store.dispatch(CustomerActions.clearPointOfContact());
    this.store.dispatch(ShopCartActions.clearPriceDisputing());
    this.store.dispatch(ShopCartActions.clearMaterialMasterNumbersForCart({cartId: cartId}));
    this.store.dispatch(CheckoutActions.clearPurchaseOrderNumber());
    this.store.dispatch(CheckoutActions.clearOrderEmailCopy());
    this.store.dispatch(CheckoutActions.resetDeliveryDetailsFormData());
    this.store.dispatch(CheckoutActions.resetDeliveryDetailsApproverFormData());
    this.store.dispatch(CheckoutActions.resetIsSaturdayShipmentSelected());
    this.store.dispatch(CheckoutActions.clearPreselectedShipToAddress());
    this.store.dispatch(CheckoutActions.clearPreselectedBillToAddress());
  }

  updateCustomerPreferences(data: ICustomerPreferences): void {
    this.store.dispatch(CustomerActions.updateCustomerPreferences({data: data}));
  }

  updateConsentedTerms(currentConsentedTerms: string[]): void {
    this.store.dispatch(CustomerActions.setConsentedTerms({newConsentedTerms: currentConsentedTerms}));
  }

  setStore(storeId: string): void {
    const [,market]: string[] = storeId.split('-');
    const currentStore: { marketCode: string } | undefined = environment.stores.find((store: { marketCode: string }) => store.marketCode === market);
    this.store.dispatch(CustomerActions.setStore({store: currentStore}));
  }

  selectStore(): Observable<any> {
    return this.store.select(CustomerSelectors.getStore);
  }

  beginGetBusinessUnitsAction(): void {
    this.store.dispatch(CustomerActions.startGetBusinessUnitAction());
  }

  selectBusinessUnitLoading(): Observable<boolean> {
    return this.store.select(CustomerSelectors.selectBusinessUnitLoading);
  }

  selectBusinessAddress(): Observable<any> {
    return this.store.select(CustomerSelectors.selectBusinessAddress);
  }

  selectBusinessUnits(): Observable<ICompanyBusinessUnitAttributes[] | undefined> {
    return this.store.select(CustomerSelectors.selectBusinessUnits);
  }

  selectCustomerLoadedGuardData(): Observable<{ loadFailed: boolean; isGuest: boolean; consentedTerms: string[] | undefined }> {
    return this.store.select(CustomerSelectors.customerLoadedGuardData);
  }

  getBusinessUnits(): Observable<ICompanyBusinessUnits> {
    return this.getCustomerService().getBusinessUnits();
  }

  beginGetCustomerAddressesAction(): void {
    this.store.select(selectUser).pipe(take(1)).subscribe(userData => {
      if (userData) {
        let userID: string = userData[AppUtils.jwtGidElementName];
        userID = userID.replaceAll('"', '');
        this.store.dispatch(CustomerActions.beginGetCustomerAddressAction({payload: `${userID}`}));
      }
    });
  }

  beginAddCustomerAddressAction(customerId: string, data: ICustomerAddressPayloadData): void {
    this.store.dispatch(CustomerActions.beginAddCustomerAddressAction({customerId, data}));
  }

  beginUpdateCustomerAddressAction(customerId: string, addressId: string, data: ICustomerAddressPayloadData): void {
    this.store.dispatch(CustomerActions.beginUpdateCustomerAddressAction({customerId, addressId, data}));
  }

  beginDeleteCustomerAddressAction(customerId: string, addressId: string, isPreferredShipToAddress: boolean): void {
    this.store.dispatch(CustomerActions.beginDeleteCustomerAddressAction({
      customerId, addressId, isPreferredShipToAddress,
    }));
  }

  selectCustomerAddresses(): Observable<IAddress[] | undefined> {
    return this.store.select(CustomerSelectors.selectCustomerAddresses);
  }

  selectUpdateCustomerAddressesInProgress(): Observable<boolean> {
    return this.store.select(CustomerSelectors.selectUpdateCustomerAddressesInProgress);
  }

  selectCustomerShipToAddresses(): Observable<any> {
    return this.store.select(CustomerSelectors.selectCustomerShipToAddresses);
  }

  setCustomCustomerShipToAddress(customerCustomerShipToAddress: IAddress): void {
    this.store.dispatch(CustomerActions.setCustomShipToAddress({data: customerCustomerShipToAddress}));
  }

  clearCustomCustomerShipToAddress(): void {
    this.store.dispatch(CustomerActions.clearCustomShipToAddress());
  }

  selectCustomCustomerShipToAddress(): Observable<IAddress | null> {
    return this.store.select(CustomerSelectors.selectCustomCustomerShipToAddress);
  }

  getCustomerAddresses(customerId: string): Observable<ICustomerAddressesData> {
    return this.getCustomerService().getCustomerAddresses(customerId.replaceAll('"', ''));
  }

  addCustomerAddress(customerId: string, payload: ICustomerAddressPayloadData): Observable<ICustomerAddressData> {
    return this.getCustomerService().addCustomerAddress(customerId, payload);
  }

  updateCustomerAddress(customerId: string, addressId: string, payload: ICustomerAddressPayloadData): Observable<ICustomerAddressData> {
    return this.getCustomerService().updateCustomerAddress(customerId, addressId, payload);
  }

  deleteCustomerAddress(customerId: string, addressId: string): Observable<any> {
    return this.getCustomerService().deleteCustomerAddress(customerId, addressId);
  }

  getAuth0CustomerFromStore(): Observable<any> {
    return this.store.select(selectUser);
  }

  saveCustomerSettingPreferences(customerId: string, data: any): Observable<any> {
    return this.getCustomerService().saveCustomerSettingPreferences(customerId, data);
  }

  deleteCustomerAccount(customerId: string): Observable<any> {
    return this.getCustomerService().deleteCustomerAccount(customerId);
  }
}
